import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

export default function Admin() {
  const navigate = useNavigate();
  const { user, loading, isAdmin } = useAuth();

  if (loading) return <div className="mx-auto max-w-5xl px-5 py-20"><div className="h-40 rounded-2xl bg-muted shimmer" /></div>;
  if (!user || !isAdmin) {
    return (
      <div className="mx-auto max-w-md px-5 py-20 text-center">
        <h1 className="font-display text-2xl font-semibold">Admin access required</h1>
        <p className="mt-2 text-sm text-muted-foreground">Sign in with an admin account to view the dashboard.</p>
        <Button onClick={() => navigate("/login")} className="mt-6 rounded-full bg-foreground px-8 text-background">Sign in</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-5 pt-8 md:pt-12">
      <h1 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">Admin dashboard</h1>
      <Tabs defaultValue="overview" className="mt-6">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="tools" data-testid="admin-tab-tools">Tools & Pricing</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="txns" data-testid="admin-tab-txns">Transactions</TabsTrigger>
          <TabsTrigger value="offers" data-testid="admin-tab-offers">Offers</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><Overview /></TabsContent>
        <TabsContent value="tools"><ToolsEditor /></TabsContent>
        <TabsContent value="users"><Users /></TabsContent>
        <TabsContent value="txns"><Txns /></TabsContent>
        <TabsContent value="offers"><Offers /></TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value }) {
  return <div className="card-soft rounded-2xl p-5"><div className="text-xs text-muted-foreground">{label}</div><div className="mt-1 font-display text-2xl font-semibold">{value}</div></div>;
}

function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <div className="mt-6 h-40 rounded-2xl bg-muted shimmer" />;
  const maxF = Math.max(...d.funnel.map((f) => f.count), 1);
  return (
    <div className="mt-6 space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Revenue (₹)" value={`₹${d.revenue}`} />
        <Stat label="Paying users" value={d.paying_users} />
        <Stat label="Total users" value={d.users} />
        <Stat label="Free → Paid" value={`${d.free_to_paid}%`} />
        <Stat label="Readings" value={d.readings} />
        <Stat label="Paid readings" value={d.paid_readings} />
        <Stat label="Rev / paying user" value={`₹${d.revenue_per_paying_user}`} />
        <Stat label="Transactions" value={d.transactions_count} />
      </div>

      <div className="card-soft rounded-2xl p-6">
        <h3 className="font-display text-lg font-semibold">Conversion funnel</h3>
        <div className="mt-4 space-y-2">
          {d.funnel.map((f) => (
            <div key={f.event} className="flex items-center gap-3">
              <span className="w-44 shrink-0 text-xs text-muted-foreground">{f.event}</span>
              <div className="h-5 flex-1 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-gold" style={{ width: `${(f.count / maxF) * 100}%` }} />
              </div>
              <span className="w-10 text-right text-sm font-medium">{f.count}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="card-soft rounded-2xl p-6">
          <h3 className="font-display text-lg font-semibold">Popular tools</h3>
          <div className="mt-3 space-y-2">{d.popular_tools.map((p) => <div key={p.tool_id} className="flex justify-between text-sm"><span>{p.tool_id}</span><span className="font-medium">{p.count}</span></div>)}</div>
        </div>
        <div className="card-soft rounded-2xl p-6">
          <h3 className="font-display text-lg font-semibold">Popular categories</h3>
          <div className="mt-3 space-y-2">{d.popular_categories.map((p) => <div key={p.category} className="flex justify-between text-sm"><span>{p.category}</span><span className="font-medium">{p.count}</span></div>)}</div>
        </div>
      </div>
    </div>
  );
}

function ToolsEditor() {
  const [tools, setTools] = useState([]);
  const load = () => api.get("/admin/tools").then(({ data }) => setTools(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async (t, patch) => {
    try { await api.put(`/admin/tools/${t.id}`, patch); toast.success(`${t.title} updated`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="mt-6 space-y-3" data-testid="admin-tools">
      {tools.map((t) => <ToolRow key={t.id} t={t} save={save} />)}
    </div>
  );
}

function ToolRow({ t, save }) {
  const [price, setPrice] = useState(t.price);
  const [desc, setDesc] = useState(t.description);
  return (
    <div className="card-soft rounded-2xl p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3"><span className="text-2xl">{t.emoji}</span><div><div className="font-medium">{t.title}</div><div className="text-xs text-muted-foreground">{t.id} • {t.category}</div></div></div>
        <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground">Enabled</span>
          <Switch data-testid={`tool-enabled-${t.id}`} checked={t.enabled} onCheckedChange={(v) => save(t, { enabled: v })} /></div>
      </div>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div><label className="mb-1 block text-xs text-muted-foreground">Price ₹</label>
          <Input data-testid={`tool-price-${t.id}`} type="number" value={price} onChange={(e) => setPrice(Number(e.target.value))} className="w-28 rounded-xl bg-background" /></div>
        <div className="min-w-[200px] flex-1"><label className="mb-1 block text-xs text-muted-foreground">Description</label>
          <Input value={desc} onChange={(e) => setDesc(e.target.value)} className="rounded-xl bg-background" /></div>
        <Button data-testid={`tool-save-${t.id}`} onClick={() => save(t, { price, description: desc })} className="rounded-full bg-foreground text-background hover:bg-foreground">Save</Button>
      </div>
    </div>
  );
}

function Users() {
  const [u, setU] = useState([]);
  useEffect(() => { api.get("/admin/users").then(({ data }) => setU(data)).catch(() => {}); }, []);
  return (
    <div className="mt-6 overflow-x-auto" data-testid="admin-users">
      <table className="w-full text-sm">
        <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2">Name</th><th>Email / Phone</th><th>Role</th><th>Joined</th></tr></thead>
        <tbody>{u.map((x) => <tr key={x.user_id} className="border-b"><td className="py-2">{x.name}</td><td>{x.email || x.phone}</td><td>{x.role}</td><td>{new Date(x.created_at).toLocaleDateString()}</td></tr>)}</tbody>
      </table>
    </div>
  );
}

function Txns() {
  const [t, setT] = useState([]);
  useEffect(() => { api.get("/admin/transactions").then(({ data }) => setT(data)).catch(() => {}); }, []);
  return (
    <div className="mt-6 overflow-x-auto" data-testid="admin-txns">
      <table className="w-full text-sm">
        <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2">Tool</th><th>Amount</th><th>Status</th><th>Mode</th><th>Date</th></tr></thead>
        <tbody>{t.map((x, i) => <tr key={i} className="border-b"><td className="py-2">{x.tool_title}</td><td>₹{x.amount}</td><td>{x.status}</td><td>{x.mode}</td><td>{new Date(x.created_at).toLocaleString()}</td></tr>)}</tbody>
      </table>
      {t.length === 0 && <p className="mt-6 text-sm text-muted-foreground">No transactions yet.</p>}
    </div>
  );
}

function Offers() {
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState({ code: "", label: "", percent_off: 10 });
  const load = () => api.get("/admin/offers").then(({ data }) => setOffers(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/admin/offers", { ...form, enabled: true }); toast.success("Offer created"); setForm({ code: "", label: "", percent_off: 10 }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="mt-6 space-y-5" data-testid="admin-offers">
      <div className="card-soft rounded-2xl p-6">
        <h3 className="font-display text-lg font-semibold">Create promotional offer</h3>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div><label className="mb-1 block text-xs text-muted-foreground">Code</label><Input data-testid="offer-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="w-36 rounded-xl bg-background" /></div>
          <div className="flex-1"><label className="mb-1 block text-xs text-muted-foreground">Label</label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="rounded-xl bg-background" /></div>
          <div><label className="mb-1 block text-xs text-muted-foreground">% off</label><Input type="number" value={form.percent_off} onChange={(e) => setForm({ ...form, percent_off: Number(e.target.value) })} className="w-24 rounded-xl bg-background" /></div>
          <Button data-testid="offer-create" onClick={create} disabled={!form.code} className="rounded-full bg-foreground text-background hover:bg-foreground">Create</Button>
        </div>
      </div>
      <div className="space-y-2">{offers.map((o) => <div key={o.code} className="card-soft flex items-center justify-between rounded-xl p-4 text-sm"><span><b>{o.code}</b> — {o.label}</span><span className="gold-text">{o.percent_off}% off</span></div>)}</div>
    </div>
  );
}
